import {
  Stack,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { type FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { useMutateCreateCategory, useMutateUpdateCategory } from '../../api/index.js';
import { type CodeCategory, CodeCategoryViewMode } from '../../models/index.js';
import { Events, humanize, track } from '../../utils/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';
import FormDialog from '../Forms/FormDialog.js';
import { type CategoryFormValues } from '../Forms/formTypes.js';
import { categoryFormSchema } from '../Forms/validations.js';


const defaultValues: CategoryFormValues = {
  name: '',
  color: 'transparent',
  showInHeaderAs: CodeCategoryViewMode.NONE,
  showInReport: false,
};

interface CategoryFormProps {
  open: boolean;
  onClose: () => void;
  category?: CodeCategory;
  refetch: () => void;
}

const CategoryForm: FC<CategoryFormProps> = ({ open, category, refetch, onClose }) => {
  const createMutation = useMutateCreateCategory();
  const updateMutation = useMutateUpdateCategory();
  const formattedData: CategoryFormValues  = useMemo(() => {
    if (category) {
      return {
        name: category.name,
        color: category.color,
        showInHeaderAs: category.showInHeaderAs,
        showInReport: category.showInReport,
      };
    }
    return defaultValues;
  }, [category]);

  return (
    <FormDialog
      open={open}
      name={'category'}
      onClose={onClose}
      data={formattedData}
      schema={categoryFormSchema}
      isEditing={!!category}
      onSubmit={async (data, isEditing) => {
        if (isEditing && category) {
          await updateMutation.mutateAsync({
            id: category.id,
            ...data,
            showInHeaderAs: data.showInHeaderAs || CodeCategoryViewMode.NONE,
          });
          track(Events.CategoryUpdate, {
            categoryId: category?.id,
          });
        } else {
          const response = await createMutation.mutateAsync({
            ...data,
            showInHeaderAs: data.showInHeaderAs || CodeCategoryViewMode.NONE,
          });
          track(Events.CategoryCreation, {
            categoryId: response.id,
          });
        }
      }}
      refetch={refetch}
    >
      {(control, errors) => (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Controller
            name={'name'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.name}
                helperText={errors?.name?.message || 'ex. Eating, Walking, etc..'}
                label={'Name'}
              />
            )}
          />
          <Controller
            name={'color'}
            control={control}
            render={({ field }) => (
              <MuiColorInput
                format={'hex'}
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.color}
                helperText={errors?.color?.message || ''}
                label={'Color'}
              />
            )}
          />
          <Controller
            name={'showInHeaderAs'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  {...field}
                  value={field.value}
                  onChange={(_: any, value: string | null) => field.onChange(value)}
                  id={'show-in-header-as-select'}
                  options={Object.keys(CodeCategoryViewMode)}
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionLabel={(option) => humanize(option)}
                  renderInput={(params) => <TextField {...params} label={'Show in Header Report'} />}
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={humanize(option)} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
          <Controller
            name={'showInReport'}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.checked)}
                  />
                }
                label="Show in Report"
              />
            )}
          />
        </Stack>
      )}
    </FormDialog>
  );
};

export default CategoryForm;
